<!--
  @copyright Copyright (c) Open-Xchange GmbH, Germany <info@open-xchange.com>
  @license AGPL-3.0

  This code is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as published by
  the Free Software Foundation, either version 3 of the License, or
  (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with OX App Suite. If not, see <https://www.gnu.org/licenses/agpl-3.0.txt>.

  Any use of the work other than as authorized under this license or copyright law is prohibited.
-->

<template>
  <div class="preview-container flex flex-col items-center">
    <div class="wrapper pt-40" :style="{'--border-mock-theme': currentTheme.border}">
      <div class="flex mb-16">
        <DarkModeToggle :isToggled="darkModeOn" @toggle="darkModeOn = !darkModeOn" />
      </div>

      <div class="email-skeleton w-full">
        <div
          class="floating-header inline-flex items-center bg-5 w-full"
          :style="{
            backgroundImage: `linear-gradient(to bottom, ${currentTheme.background}, ${currentTheme.background5})`,
            text: currentTheme.text
        }">
          <span class="font-semibold text-base py-16">{{ i18n.skeletonHeader }}</span>
        </div>

        <div class="placeholder recipient">
          <div class="bar"></div>
        </div>
        <div class="placeholder recipient">
          <div class="bar"></div>
        </div>

        <div class="signature-wrapper flex items-end">
          <div class="signature-preview flex w-full">
            <Transition mode="out-in">
              <component
                style=" overflow-wrap: anywhere;"
                v-if="templateComponent"
                :is="templateComponent"
                id="signature-preview"
                :data="contactData"
                ref="template"
              />
            </Transition>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, watch, ref, nextTick } from 'vue'
import { storeToRefs } from 'pinia'
import { useSignatureDesignerStore } from '@/io.ox/mail/settings/signatures/signature-designer/store/signature-designer-store'

import DarkModeToggle from '@/io.ox/mail/settings/signatures/signature-designer/components/dark-mode-toggle.vue'
import templates from '@/io.ox/mail/settings/signatures/signature-designer/templates'

import gt from 'gettext'

const store = useSignatureDesignerStore()
const { contactData, currentSignatureIndex, availableFields } = storeToRefs(store)
const template = ref(null)

const templateComponent = computed(() => {
  const id = String(currentSignatureIndex.value).padStart(3, '0')
  return templates[id]
})

// retrieve template data and update fields in store
watch(template, (newTemplate) => {
  if (!newTemplate?.availableFields) return
  availableFields.value = newTemplate.availableFields
})

// allow for tab-navigation in mails, but disable here in the preview
watch(template, async (newTemplate) => {
  if (!newTemplate?.$el) return

  await nextTick()

  const focusables = newTemplate.$el.querySelectorAll(
    'a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
  )

  focusables.forEach(el => el.setAttribute('tabindex', '-1'))
})

const darkModeOn = ref(false)
const darkTheme = {
  background: '#222',
  background5: '#2a2a2a',
  text: '#b0b0b0',
  shade10: 'hsla(0, 100%, 100%, 10%)',
  primaryShade: 'var(--accent-200)',
  border: '#444'
}
const whiteTheme = {
  background: 'white',
  background5: 'hsl(40, 10%, 95%)',
  text: '#212529',
  shade10: 'hsla(0, 0%, 0%, 10%)',
  primaryShade: 'var(--accent-50)',
  border: 'hsl(40, 10%, 80%)'
}
const currentTheme = computed(() => {
  return darkModeOn.value ? darkTheme : whiteTheme
})

const i18n = {
  skeletonHeader: gt('New email')
}
</script>

<style lang="scss" scoped>
.preview-container {
  width: 60%;
  background-color: var(--accent-a10);
  overflow: auto;
}

.wrapper {
  width: 80%;

  .email-skeleton {
    border-radius: var(--double-radius);
    background-color: v-bind('currentTheme.background');

    & > div { padding-left: 12px; }

    .floating-header {
      border-radius: var(--double-radius) var(--double-radius) 0 0;
      background-image: linear-gradient(to bottom, var(--background), var(--background-5));
      height: 48px;
      color: v-bind('currentTheme.text');
    }

    .placeholder {
      height: 48px;
      display: flex;
      align-items: center;

      .bar {
        height: 20px;
        border-radius: var(--default-radius);
      }

      &.recipient {
        .bar {
          width: 80px;
          background-color: v-bind('currentTheme.shade10');
        }
        border-bottom: 1px solid var(--border-mock-theme);
      }

      &.action  {
        border-top: 1px solid var(--border-mock-theme);
        .bar {
          width: 120px;
          background-color: v-bind('currentTheme.primaryShade');
        }
      }
    }

    .signature-wrapper {
      min-height: 250px;
      padding: 64px 8px 24px 12px;

      .signature-preview {
        color: v-bind('currentTheme.text');
        :deep(a) { color: v-bind('currentTheme.text'); } // needed for overwriting css-specificity / css-order
      }
    }
  }
}
</style>
